'use client'

import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useQuery } from '@apollo/client'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { Sheet } from '@/components/ui/sheet'
import { CustomerAccount } from './types'
import { columns } from './components/columns'
import { FIND_MANY_CUSTOMER_ACCOUNT } from './customerAccountQueries'
import CustomerAccountForm from './CustomerAccountForm'
import { CustomerAccountActions } from './components/CustomerAccountActions'

interface CustomerAccountTableProps {
  customerId: number
}

export default function CustomerAccountTable({
  customerId,
}: CustomerAccountTableProps) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<CustomerAccount | null>(
    null
  )

  const {
    data: { findManyCustomerAccount: customerAccounts } = {},
    loading,
    error,
  } = useQuery(FIND_MANY_CUSTOMER_ACCOUNT, {
    variables: { customerId },
    fetchPolicy: 'cache-and-network',
  })

  const handleAddAccount = () => {
    setEditingAccount(null)
    setIsFormOpen(true)
  }

  const handleEditAccount = (account: CustomerAccount) => {
    setEditingAccount(account)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingAccount(null)
  }

  // Create enhanced columns with edit handler
  const enhancedColumns = columns.map(column => {
    if (column.id === 'actions') {
      return {
        ...column,
        cell: ({ row }: { row: { original: CustomerAccount } }) => (
          <CustomerAccountActions
            customerAccount={row.original}
            onEdit={handleEditAccount}
          />
        ),
      }
    }
    return column
  })

  if (error) {
    return (
      <div className="text-red-600">
        Error loading customer accounts: {error.message}
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium">User Accounts</h3>
          <Button type="button" onClick={handleAddAccount} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>

        <DataTableManual
          columns={enhancedColumns}
          data={customerAccounts || []}
          rowCount={customerAccounts?.length || 0}
          pageIndex={0}
          pageSize={10}
          onPaginationChange={() => {}}
          onSortingChange={() => {}}
          onColumnFiltersChange={() => {}}
          tableTextSize="text-xs"
          isLoading={loading}
          emptyStateLabel="No user accounts found"
        />
      </div>

      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <CustomerAccountForm
          data={{
            id: editingAccount?.id,
            customerId,
          }}
          onRequestClose={handleCloseForm}
        />
      </Sheet>
    </>
  )
}
